import { isBrowser, fadeUpEffect } from '@publicss/utils';
import { Link } from 'gatsby';
import { gsap } from 'gsap';
import parse from 'html-react-parser';
import PropType from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { Button } from '../../../components/ui';
import { usePageContext } from '../../../providers/PageContext';
import src from '../../assets/image--blog-1.jpg';
import FacebookSVG from '../../assets/svg--facebook';
import LinkedInSVG from '../../assets/svg--linkedin';
import TelegramSVG from '../../assets/svg--telegram';
import TwitterSVG from '../../assets/svg--twitter';
import WhatsAppSVG from '../../assets/svg--whatsapp';
import { IntlBlogTypographyStyles } from '../../latam-blog/components/BlogPost';
import { blogPath, blogArticlePath, getCurrURL } from '../utils/routes';

const Section = styled.section`
  ${IntlBlogTypographyStyles}

  opacity: 0;
  figure {
    margin-bottom: 30px;
  }
  .wp-block-image {
    & > span,
    .gatsby-image-wrapper {
      width: 100% !important;
    }
    span[aria-hidden="true"],
    img[aria-hidden="true"] {
      display: none !important;
    }
    span img {
      position: static !important;
      width: 100% !important;
      height: auto !important;
    }
    picture {
      display: block;
    }
  }
  .blog-article-body {
    a {
      text-decoration: underline;
      color: var(--primary);

      &:hover {
        color: var(--primary-dark);
      }
    }
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p,
    ul,
    ol {
      margin-bottom: 30px;
    }
    ul,
    ol {
      li:not(:only-child) {
        margin-inline-start: 15px;
        margin-bottom: 15px;
        padding-inline-start: 20px
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
    ul li:not(:only-child) {
      list-style: disc;
    }
    ol li:not(:only-child) {
      list-style: decimal;
    }
  }
  .line--clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const BlogPost = (props) => {
  React.useEffect(() => {
    const fadeElements = gsap.timeline();

    fadeElements.to('#block-hero-blog', {
      opacity: 1,
      delay: 0.25,
    });

    fadeUpEffect('#block-hero-blog .fade-up');
  }, []);

  const { siteTag, siteLang } = usePageContext();
  const siteLangCode = siteLang.split('-')[0];

  const blogLang =
    siteTag === 'site:citychefs' && ['pt', 'it'].includes(siteLangCode)
      ? siteLangCode
      : siteTag === 'site:kitchenpark' && ['ar', 'en'].includes(siteLangCode)
        ? siteLangCode
        : undefined;

  const { previous, next, post } = props;

  const previousFeaturedImage =
    previous && previous.featuredImage
      ? {
          src: previous.featuredImage?.node?.localFile?.childImageSharp?.fluid
            ?.src,
        }
      : { src };

  const nextFeaturedImage =
    next && next.featuredImage
      ? {
          src: next.featuredImage?.node?.localFile?.childImageSharp?.fluid?.src,
        }
      : { src };

  const currentURL = isBrowser() ? getCurrURL() : '';

  return (
    <Section id="block-hero-blog">
      <div className="pt-30 container pb-20 pt-4 md:pb-20 md:pt-20 lg:pb-40 lg:pt-48">
        <div className="grid sm:grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <div className="col-span-4 md:col-span-3 lg:col-span-5">
            <div className="eyebrow color--grey-4 fade-up mb-4">
              {post.date}
            </div>

            <h1 className="h2 color--main mb-30 fade-up">
              {parse(post.title)}
            </h1>

            <div className="fade-up mt-6 flex items-center">
              <div className="flex">
                <a
                  href={`https://twitter.com/intent/tweet?url=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db mr-3">
                  <TwitterSVG width="16" height="16" fill="#777777" />
                </a>

                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db mr-3">
                  <FacebookSVG width="16" height="16" fill="#777777" />
                </a>

                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db mr-3">
                  <LinkedInSVG width="16" height="16" fill="#777777" />
                </a>

                <a
                  href={`https://t.me/share/url?url=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db mr-3">
                  <TelegramSVG width="16" height="16" fill="#777777" />
                </a>

                <a
                  href={`https://wa.me/?text=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db mr-3">
                  <WhatsAppSVG width="16" height="16" fill="#777777" />
                </a>
              </div>

              <div className="border-l border-grey-3 pl-2">
                <Link
                  to={blogPath(siteTag, blogLang)}
                  className="btn--rounded btn--rounded--slim btn--border btn--border--grey-3 color--grey-6 db"
                  direction="up"
                  duration="1"
                  bg="#E4E2D4">
                  View all
                </Link>
              </div>
            </div>
          </div>

          <div className="col-span-4 pt-6 md:col-start-5 lg:col-span-6 lg:col-start-7">
            {post.content && (
              <section className="blog-article-body" itemProp="articleBody">
                <div
                  dangerouslySetInnerHTML={{
                    __html: post.content,
                  }}
                />
              </section>
            )}
          </div>

          <div className="col-span-4 md:col-span-8 lg:col-span-12 lg:my-40">
            <hr className="my-20" />
          </div>

          <div className="col-span-4 md:col-span-3 lg:col-span-4">
            <h2 className="h2 color--main mb-30 fade-up">
              More insights & stories
            </h2>
          </div>

          <div className="col-span-4 md:col-start-5 lg:col-span-6 lg:col-start-7">
            <p className="mb-12">
              There’s more where that came from. Get in the know and check out
              our addtional insights
            </p>

            <div className="flex items-center">
              <Button href="#">Get Started</Button>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-4 gap-x-10 gap-y-10 md:grid-cols-12">
          {previous && (
            <div className="col-span-4 md:col-span-6">
              <Link
                to={
                  siteTag === 'site:citychefs' && previous.uri.includes('/it/')
                    ? blogArticlePath(siteTag, previous.uri, 'it')
                    : blogArticlePath(siteTag, previous.uri, blogLang)
                }
                className="blog--card bg--white fade-up block lg:flex lg:pt-12">
                <div className="blog--card--image flex-none">
                  <div
                    className="blog--card--bg"
                    style={{
                      backgroundImage: `url(${previousFeaturedImage.src})`,
                    }}
                  />
                </div>

                <div className="p-6 md:pl-12 md:pr-12 md:pt-8 lg:pb-2 lg:pl-8 lg:pr-14 lg:pt-0">
                  <div className="eyebrow color--grey-4 mb-6">
                    {previous.date}
                  </div>

                  <p className="h5 line--clamp mb-6">{parse(previous.title)}</p>

                  <span className="btn--text--svg color--main">
                    <span>{/* Learn More */}</span>

                    <svg
                      width="8"
                      height="8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path d="M.305.348l6.81.013.012 6.81H5.83l.05-4.608-4.848 4.85-.968-.968 4.85-4.85-4.608.052V.348z" />
                    </svg>
                  </span>
                </div>
              </Link>
            </div>
          )}

          {next && (
            <div className="col-span-4 md:col-span-6">
              <Link
                to={blogArticlePath(siteTag, next.uri, blogLang)}
                className="blog--card bg--white fade-up block lg:flex lg:pt-12">
                <div className="blog--card--image flex-none">
                  <div
                    className="blog--card--bg"
                    style={{
                      backgroundImage: `url(${nextFeaturedImage.src})`,
                    }}
                  />
                </div>

                <div className="p-6 md:pl-12 md:pr-12 md:pt-8 lg:pb-2 lg:pl-8 lg:pr-14 lg:pt-0">
                  <div className="eyebrow color--grey-4 mb-6">{next.date}</div>

                  <p className="h5 line--clamp mb-6">{parse(next.title)}</p>

                  <span className="btn--text--svg color--main">
                    <span>{/* Learn More */}</span>

                    <svg
                      width="8"
                      height="8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path d="M.305.348l6.81.013.012 6.81H5.83l.05-4.608-4.848 4.85-.968-.968 4.85-4.85-4.608.052V.348z" />
                    </svg>
                  </span>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

BlogPost.propTypes = {
  previous: PropType.object,
  next: PropType.object,
  post: PropType.shape({
    title: PropType.string,
    content: PropType.string,
    date: PropType.string,
  }),
};

export default BlogPost;
